import {ErrorMessage, Field, useField} from 'formik';
import React from 'react';
import styled from 'styled-components';

import {FormGroup} from '../../../form-containers/FormGroup';
import {FormErrorMessage} from '../../../form-labels/FormErrorMessage';
import {FormLabel} from '../../../form-labels/FormLabel';
import {SelectionControl, SelectionControlGroup} from '../SelectionControl';

/**
 * The Radio component represents a parent container for a group of radio options.
 *
 * This uses Formik, so this must be used within a <Formik> container.
 *
 * @param {object} param0
 *
 * @param {string} [param0.name]
 * The name to use for the form field.
 *
 * @param {React.ReactNode?} [param0.label]
 * The label meant for the entire radio group, acting as the topic name.
 *
 * @param {React.ReactNode} param0.children
 *
 * @example
 * ```jsx
 *      // == --- How to use this component:  --- ==
 *       <Radio name="someFieldName" label="Radio Example">
 *          {options.map((eo, ix) => (
 *          // Note: name must match the Radio name, because this is used to assign the value.
 *          <Radio.Option key={eo.value} name="someFieldName" value={eo.value} label={eo.label} />
 *          ))}
 *      </Radio>,
 * ```
 */
export const Radio = ({name, label, children}) => (
    <FormGroup>
        <FormLabel>
            {label && <span>{label}</span>}
            {children}
        </FormLabel>
        <ErrorMessage name={name} component={FormErrorMessage} />
    </FormGroup>
);

/**
 * The RadioOption component represents a single radio option within a radio group.
 * It renders a radio input along with a label, allowing users to select a specific option.
 *
 * @param {object} param0
 *
 * @param {boolean?} [param0.autoFocus]
 * Sets the autofocus for this component
 *
 * @param {boolean?} [param0.disabled]
 * Whether this option is disabled
 *
 * @param {string} [param0.name]
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik Input component is essential because Formik uses the name prop to link the value of the  input to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {any} [param0.value]
 * The value that the option assigns to the field.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {number?} [param0.level]
 * The level of the radio option within a nested hierarchy, if applicable.
 */
export const RadioOption = ({autoFocus, disabled, name, value, label, level}) => {
    const [field, , helper] = useField(name);

    return (
        <Wrapper radio level={level} label={label}>
            <Field
                autoFocus={autoFocus}
                disabled={disabled}
                checked={value === field.value}
                name={field.name}
                type="radio"
                // onChange() is called (according to the official spec):
                // - when a user changes it (so not programatically),
                // - when the radio is checked (not unchecked),
                // - after the value of the element has been changed.
                // So, it's safe to use this to assign a new field value.
                onChange={() => helper.setValue(value)}
            />
        </Wrapper>
    );
};

const Wrapper = styled(SelectionControl)`
    margin-bottom: var(--space-md);

    ${SelectionControlGroup} {
        margin-bottom: 0;
    }
`;

Radio.Option = RadioOption;
